import {showNotification} from 'platform/components';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {ServiceCaseDetailBootstrapBoschFleetIntegration} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {logError} from '@dms/shared';

import {Nullish} from 'shared/web';

const TERMS_PDF_URL_REVOKE_DELAY_MS = 60_000;
const PDF_CONTENT_TYPE = 'application/pdf';

export function useOpenBoschFleetTermsAndConditions(
  boschFleetIntegration: ServiceCaseDetailBootstrapBoschFleetIntegration | Nullish
) {
  const openTermsAndConditionsDocument = () => {
    const url = createTermsPdfObjectUrl(boschFleetIntegration);
    if (isNil(url)) {
      showNotification.error(
        i18n.t('entity.customer.notifications.boschFleetTermsAndConditionsNotAvailable')
      );
      return;
    }
    window.setTimeout(() => window.URL.revokeObjectURL(url), TERMS_PDF_URL_REVOKE_DELAY_MS);
    window.open(url, '_blank');
  };

  return {
    isTermsAndConditionsDocumentAvailable: hasOpenablePdfDocument(boschFleetIntegration),
    openTermsAndConditionsDocument,
  };
}

function hasOpenablePdfDocument(
  boschFleetIntegration: ServiceCaseDetailBootstrapBoschFleetIntegration | Nullish
): boschFleetIntegration is ServiceCaseDetailBootstrapBoschFleetIntegration & {body: string} {
  return (
    isNotNil(boschFleetIntegration) &&
    isNotNilOrEmpty(boschFleetIntegration.body) &&
    (boschFleetIntegration.contentType?.toLowerCase().startsWith(PDF_CONTENT_TYPE) ?? false)
  );
}

function createTermsPdfObjectUrl(
  boschFleetIntegration: ServiceCaseDetailBootstrapBoschFleetIntegration | Nullish
) {
  if (!hasOpenablePdfDocument(boschFleetIntegration)) {
    return null;
  }
  try {
    const bytes = Uint8Array.from(atob(boschFleetIntegration.body), (character) =>
      character.charCodeAt(0)
    );
    return window.URL.createObjectURL(new Blob([bytes], {type: PDF_CONTENT_TYPE}));
  } catch (error) {
    logError(
      `Bosch fleet T&C document failed to decode: ${error instanceof Error ? error.message : String(error)}`
    );
    return null;
  }
}
